import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  User, Lock, Shield, Eye, EyeOff, Loader2, Check, X 
} from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettingsTab = ({ user, token, onUserUpdate }) => {
  // Tab state
  const [activeSection, setActiveSection] = useState("profile");

  // Profile state
  const [name, setName] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setTwoFAEnabled(user.totp_enabled || false);
    }
  }, [user]);

  const sections = [
    { id: "profile", label: "Профиль", icon: User },
    { id: "password", label: "Пароль", icon: Lock },
    { id: "2fa", label: "Google Authenticator", icon: Shield },
  ];

  // Profile handlers
  const handleProfileSave = async () => {
    if (!name.trim()) {
      toast.error("Введите имя");
      return;
    }

    setProfileLoading(true);
    try {
      await axios.put(
        `${API}/settings/profile`,
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUserUpdate) await onUserUpdate();
      toast.success("Профиль обновлён");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setProfileLoading(false);
    }
  };

  // Password handlers
  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Заполните все поля");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Пароли не совпадают");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Пароль должен быть не менее 6 символов");
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(
        `${API}/settings/password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Пароль изменён");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка смены пароля");
    } finally {
      setPasswordLoading(false);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const res = await axios.post(
        `${API}/settings/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setSetupMode(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка настройки 2FA");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (otpCode.length !== 6) {
      toast.error("Введите 6-значный код");
      return;
    }

    setTwoFALoading(true);
    try {
      await axios.post(
        `${API}/settings/2fa/verify`,
        {
          code: otpCode,
          secret: secret,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUserUpdate) await onUserUpdate();
      setTwoFAEnabled(true);
      setSetupMode(false);
      setQrCode(null);
      setSecret(null);
      setOtpCode("");
      toast.success("2FA включена!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Неверный код");
      setOtpCode("");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error("Введите пароль");
      return;
    }

    setTwoFALoading(true);
    try {
      await axios.post(
        `${API}/settings/2fa/disable`,
        { password: disablePassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUserUpdate) await onUserUpdate();
      setTwoFAEnabled(false);
      setShowDisableModal(false);
      setDisablePassword("");
      toast.success("2FA отключена");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Неверный пароль");
    } finally {
      setTwoFALoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "A";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white mb-6">Настройки администратора</h2>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeSection === section.id
                  ? "bg-[#FF8A1E] text-white"
                  : "bg-[#1A1A1A] text-[#A1A1AA] hover:text-white"
              }`}
            >
              <Icon size={18} />
              {section.label}
            </button>
          );
        })}
      </div>

      {/* Profile Section */}
      {activeSection === "profile" && (
        <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#1F1F1F]">
          <h3 className="text-lg font-semibold text-white mb-4">Профиль администратора</h3>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2E2E2E] to-[#1A1A1A] flex items-center justify-center text-xl font-bold text-white">
              {getInitials(name)}
            </div>
            <div>
              <p className="text-white font-medium">{user?.email}</p>
              <p className="text-[#A1A1AA] text-sm">Администратор</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Имя</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:border-white focus:outline-none"
                placeholder="Введите имя"
              />
            </div>

            <button
              onClick={handleProfileSave}
              disabled={profileLoading}
              className="px-6 py-3 bg-[#FF8A1E] text-white font-medium rounded-lg hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {profileLoading ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
              Сохранить
            </button>
          </div>
        </div>
      )}

      {/* Password Section */}
      {activeSection === "password" && (
        <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#1F1F1F]">
          <h3 className="text-lg font-semibold text-white mb-4">Изменить пароль</h3>
          
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Текущий пароль</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-4 py-3 pr-12 text-white focus:border-white focus:outline-none"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Новый пароль</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-4 py-3 pr-12 text-white focus:border-white focus:outline-none"
                  placeholder="Минимум 6 символов"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Подтвердите пароль</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:border-white focus:outline-none"
                placeholder="Повторите новый пароль"
              />
            </div>

            <button
              onClick={handlePasswordChange}
              disabled={passwordLoading}
              className="px-6 py-3 bg-[#FF8A1E] text-white font-medium rounded-lg hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {passwordLoading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />}
              Изменить пароль
            </button>
          </div>
        </div>
      )}

      {/* 2FA Section */}
      {activeSection === "2fa" && (
        <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#1F1F1F]">
          <h3 className="text-lg font-semibold text-white mb-4">Google Authenticator</h3>
          
          {!twoFAEnabled && !setupMode && (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F]">
                <Shield className="text-[#EDEDED] flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="text-white font-medium mb-1">Двухфакторная аутентификация</p>
                  <p className="text-[#A1A1AA] text-sm">
                    Защитите аккаунт администратора с помощью Google Authenticator. 
                    При входе потребуется ввести код из приложения.
                  </p>
                </div>
              </div>

              <button
                onClick={handleSetup2FA}
                disabled={twoFALoading}
                className="px-6 py-3 bg-[#FF8A1E] text-white font-medium rounded-lg hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {twoFALoading ? <Loader2 className="animate-spin" size={18} /> : <Shield size={18} />}
                Настроить 2FA
              </button>
            </div>
          )}

          {setupMode && (
            <div className="space-y-6">
              <div className="flex flex-col items-center">
                <p className="text-[#A1A1AA] text-sm mb-4 text-center">
                  Отсканируйте QR-код в приложении Google Authenticator
                </p>
                {qrCode && (
                  <div className="bg-white p-4 rounded-lg">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                )}
                {secret && (
                  <div className="mt-4 text-center">
                    <p className="text-[#A1A1AA] text-xs mb-1">Или введите код вручную:</p>
                    <code className="text-[#EDEDED] text-sm bg-[#0A0A0A] px-3 py-1 rounded">{secret}</code>
                  </div>
                )}
              </div>

              <div className="max-w-xs mx-auto">
                <label className="block text-[#A1A1AA] text-sm mb-2 text-center">
                  Введите код из приложения
                </label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={(value) => setOtpCode(value)}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setSetupMode(false);
                    setQrCode(null);
                    setSecret(null);
                    setOtpCode("");
                  }}
                  className="px-4 py-2 bg-[#1F1F1F] text-white rounded-lg hover:bg-[#3E3E3E] transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={handleVerify2FA}
                  disabled={twoFALoading || otpCode.length !== 6}
                  className="px-6 py-2 bg-[#FF8A1E] text-white font-medium rounded-lg hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {twoFALoading ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                  Подтвердить
                </button>
              </div>
            </div>
          )}

          {twoFAEnabled && !setupMode && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-[#0D3320] rounded-lg border border-[#22C55E]/30">
                <Check className="text-[#22C55E]" size={24} />
                <div>
                  <p className="text-white font-medium">2FA включена</p>
                  <p className="text-[#A1A1AA] text-sm">Ваш аккаунт защищён двухфакторной аутентификацией</p>
                </div>
              </div>

              <button
                onClick={() => setShowDisableModal(true)}
                className="px-4 py-2 bg-[#1F1F1F] text-[#F87171] rounded-lg hover:bg-[#3E3E3E] transition-colors flex items-center gap-2"
              >
                <X size={18} />
                Отключить 2FA
              </button>
            </div>
          )}

          {/* Disable Modal */}
          {showDisableModal && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <div className="bg-[#1A1A1A] rounded-xl p-6 w-full max-w-sm border border-[#1F1F1F]">
                <h4 className="text-lg font-semibold text-white mb-4">Отключить 2FA</h4>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  Введите пароль для подтверждения
                </p>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white mb-4 focus:border-white focus:outline-none"
                  placeholder="Введите пароль"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDisableModal(false);
                      setDisablePassword("");
                    }}
                    className="flex-1 px-4 py-2 bg-[#1F1F1F] text-white rounded-lg hover:bg-[#3E3E3E] transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleDisable2FA}
                    disabled={twoFALoading}
                    className="flex-1 px-4 py-2 bg-[#F87171] text-white rounded-lg hover:bg-[#C53030] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {twoFALoading ? <Loader2 className="animate-spin" size={18} /> : null}
                    Отключить
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSettingsTab;
